import React from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaCheckCircle } from 'react-icons/fa';

const Hero = () => {
  return (
    <section className="bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
      {/* Spacing za fixed header - 96px */}
      <div className="h-24"></div>

      {/* Pozadinski geometrijski elementi */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-2xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-cyan-500/3 rounded-full blur-3xl"></div>

        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]"></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 text-center py-16">
        {/* Animirani naslov */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent">
              Automatizujemo 80% komunikacije
            </span>
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              u inboxu za 14 dana ili radimo besplatno.
            </span>
          </h1>
        </motion.div>

        {/* Podnaslovi */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-300 mb-6 max-w-4xl mx-auto font-light leading-relaxed">
            Za e-commerce shopove u Srbiji: chatbot odgovara za ~3 sekunde, 24/7, a ti se uključuješ samo kad stigne kompleksan upit.
          </p>
        </motion.div>

        {/* CTA sekcija */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-8">
            <a
              href="https://www.instagram.com/eagleai_agents/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2 shadow-lg shadow-blue-600/25"
            >
              <span>Pošalji CHATBOT u DM</span>
              <FaArrowRight />
            </a>
            <a
              href="#kontakt"
              className="w-full sm:w-auto border border-gray-600 hover:border-gray-400 text-gray-300 hover:text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 hover:bg-gray-900/50 text-center"
            >
              <span>Zakaži audit</span>
            </a>
          </div>

          <p className="text-xs sm:text-sm text-gray-500 px-4">
            *Rezultati zavise od ponude i volumena upita. Garancija se odnosi na procenat automatizovanih ponavljajućih upita.
          </p>
        </motion.div>

        {/* Performance statistike */}
        <motion.div
          className="grid grid-cols-3 gap-4 sm:gap-8 mb-8 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="text-center">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-400 mb-2">~3s</div>
            <div className="text-gray-400 text-xs sm:text-sm">Response Time</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-cyan-400 mb-2">82%</div>
            <div className="text-gray-400 text-xs sm:text-sm">Automated</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-green-400 mb-2">+24%</div>
            <div className="text-gray-400 text-xs sm:text-sm">Sales Conversion</div>
          </div>
        </motion.div>

        {/* Sekcija sa ključnim prednostima */}
        <motion.div
          className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-y-3 gap-x-6 px-4 pb-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="flex items-center space-x-2 text-gray-400 text-sm sm:text-base">
            <FaCheckCircle className="text-green-500 flex-shrink-0" />
            <span>Vraćamo 40+ sati mesečno</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-400 text-sm sm:text-base">
            <FaCheckCircle className="text-green-500 flex-shrink-0" />
            <span>Brži odgovori → manje izgubljenih prodaja</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-400 text-sm sm:text-base">
            <FaCheckCircle className="text-green-500 flex-shrink-0" />
            <span>Dostava / povrat / status porudžbine</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;